import {
  ComponentProps,
  Dispatch,
  FC,
  ReactElement,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { BasePill, StyledBasePill } from "./BasePill";

enum GroupedPillTestIds {
  Container = "grouped-pill",
}

export type VerticalPosition = "top" | "bottom";
export type HorizontalPosition = "left" | "right";

type GroupedPillProps = {
  children: ReactElement<ComponentProps<typeof BasePill>>[];
  className?: string;
  testId?: string;
  id?: string;
  verticalPosition?: VerticalPosition;
  horizontalPosition?: HorizontalPosition;
  openOnHover?: boolean;
};

export const GroupedPill: FC<GroupedPillProps> = ({
  children,
  className,
  testId,
  id,
  verticalPosition = "bottom",
  horizontalPosition = "right",
  openOnHover = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const summaryPillWrapperRef = useRef<HTMLDivElement>(null);
  const pillDropdownRef = useRef<HTMLDivElement>(null);

  useClickAwayListener(setIsOpen, summaryPillWrapperRef, pillDropdownRef);

  return (
    <SummaryPillWrapper
      {...(!openOnHover && { onClick: () => setIsOpen((isOpen) => !isOpen) })}
      ref={summaryPillWrapperRef}
      data-testid={testId ?? GroupedPillTestIds.Container}
      id={id}
      className={className}
      {...(openOnHover && {
        onMouseEnter: () => {
          setIsOpen(true);
        },
        onMouseLeave: () => setIsOpen(false),
      })}
    >
      <SummaryPill {...{ ...summaryPillColors, isOpen }}>
        {`+${children.length}`}
      </SummaryPill>
      {isOpen && (
        <PillDropdown
          ref={pillDropdownRef}
          style={{
            ...(verticalPosition === "top"
              ? { bottom: "100%" }
              : { top: "100%" }),
            ...(horizontalPosition === "left" ? { right: 0 } : { left: 0 }),
          }}
        >
          {children}
        </PillDropdown>
      )}
    </SummaryPillWrapper>
  );
};

const SummaryPillWrapper = styled.div`
  display: inline-block;
  position: relative;
  &:hover {
    cursor: pointer;
  }
`;

const summaryPillColors = {
  color: "var(--color-secondary-900)",
  bgColor: "var(--color-secondary-100)",
};
const SummaryPill = styled(StyledBasePill)<{ isOpen: boolean }>`
  border: 1px solid;
  ${({ isOpen }) =>
    css`
      border-color: var(
        ${isOpen ? "--color-secondary-800" : "--color-primary-100"}
      );
      background-color: var(
        ${isOpen ? "--color-secondary-100" : "--color-neutral-000-full"}
      );
    `}
  &:hover {
    border-color: var(--color-secondary-800);
    background-color: var(--color-secondary-100);
  }
`;

const PillDropdown = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  position: absolute;
  z-index: 1;
  width: max-content;
  max-height: 250px;
  overflow: auto;
  background-color: var(--color-neutral-000-full);
  border-radius: var(--size-border-radius-small);
  box-shadow: var(--elevation-4);
  padding: 12px 8px;
  & > * {
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const useClickAwayListener = (
  setOpen: Dispatch<SetStateAction<boolean>>,
  summaryPillWrapperRef: RefObject<HTMLDivElement>,
  pillDropdownRef: RefObject<HTMLDivElement>
) => {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        summaryPillWrapperRef.current &&
        summaryPillWrapperRef.current.contains(event.target as Node)
      )
        return;
      if (
        pillDropdownRef.current &&
        !pillDropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return (): void => {
      document.removeEventListener("click", handleClickOutside);
    };
  });
};
